import { IAssetDetails } from 'models/assets/i-asset-details.interface';
import { IStoreAssetApiResponse } from 'models/store/i-store-asset-api-response.interface';
import { IWorldNftTradeResponse } from 'models/store/i-world-nft-trade-response.interface';
import { ITradeDetails } from 'models/trade/i-trade-details.interface';
import { TradeOfferStatus } from 'models/trade/i-trade-offer.interface';
import { ITradePreviewDetails } from 'models/trade/i-trade-preview-details.interface';

import { selectRandomUnusedOffer } from './select-unused-offer.utils';
import { getVariationByShares } from './trade-variations.utils';
import { getBaseVariationData, isWorldNftTradeSuccess } from './world-nft/world-nft.utils';

/**
 * Converts World NFT Trade data to ITradePreviewDetails format
 * Combines data from both /api/v1/trades/world-nft/ and /api/v1/assets/all_assets
 * Returns null if the trade data is in error state (sold out/not found)
 */
export const convertWorldNftTradeToTradePreviewDetails = async (
  worldNftTrade: IWorldNftTradeResponse,
  assetData: IStoreAssetApiResponse,
  selectedShares?: number
): Promise<ITradePreviewDetails | null> => {
  // Handle error response (sold out or not found)
  if (!isWorldNftTradeSuccess(worldNftTrade)) {
    return null;
  }

  // Get variation data for pricing - use selected shares if provided, otherwise use base
  const selectedVariation = selectedShares
    ? getVariationByShares(worldNftTrade.available_trade_offers_by_variation, selectedShares)
    : getBaseVariationData(worldNftTrade);

  if (!selectedVariation || selectedVariation.available_trade_offers.length === 0) {
    return null;
  }

  // Randomly select an unused offer from available offers
  const selectedTradeOffer = await selectRandomUnusedOffer(selectedVariation.available_trade_offers);

  // If no available offer found, return null
  if (!selectedTradeOffer) {
    console.warn('No available offers found - all signatures have been used');
    return null;
  }

  // Create asset details combining both APIs
  const assetDetails: IAssetDetails = {
    id: worldNftTrade.world_nft.id,
    address: assetData.address,
    name: worldNftTrade.world_nft.name,
    token_id: worldNftTrade.world_nft.token_id,
    image_original_url: assetData.image_original_url,
    animation_url: assetData.animation_url!,
    marker_url: assetData.marker_url,
    traits: assetData.traits,
    total_shares: selectedVariation.total_shares,
    contract: assetData.contract,
    shares_currently_on_sale: selectedVariation.available_quantity,
    description: assetData.description,
    asset_original_url: assetData.asset_original_url || assetData.image_original_url,
    active_trades: [],
    owned_shares: assetData.owned_shares,
    active_shares: selectedVariation.total_shares,
    owners_count: 1, // TODO: This would need to come from a different endpoint
    nft_value: parseFloat(selectedVariation.price) / Math.pow(10, worldNftTrade.currency.decimal),
  };

  // Create trade details from the selected trade offer
  const tradeDetails: ITradeDetails = {
    id: worldNftTrade.id,
    asset_id: worldNftTrade.world_nft.id,
    amount: selectedVariation.amount_multiplier.toString(),
    currency_id: worldNftTrade.currency.id,
    expiration_time: selectedTradeOffer.expiration_time,
    nonce: selectedTradeOffer.nonce,
    price: selectedVariation.price,
    seller: {
      id: worldNftTrade.seller.id,
      address: worldNftTrade.seller.address,
    },
    signature: selectedTradeOffer.signature,
    signature_hash: selectedTradeOffer.signature_hash,
    state_hash: '0x0',
    status: TradeOfferStatus.ACTIVE,
    asset: assetDetails as any, // TODO: Fix proper type mapping
    auction_house_expiration_datetime: null,
    auction_id: null,
    contract_type: worldNftTrade.contract_type,
    owned_awaiting_offer: null,
    stable_exchange_rate: null,
    stats: {
      owned_offers: 0,
      owned_offers_active: 0,
      others_offers: selectedVariation.available_trade_offers.length,
      others_offers_active: selectedVariation.available_trade_offers.length,
    },
    type: 0 as any, // TODO: Map to proper VenueTypes
  };

  return {
    assetDetails,
    tradeDetails,
  };
};
